// PlanifLinesTableros.js
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import apiClient from "../api/apiClient";
import Kanban from "../components/Kanban"; // Componente Kanban reutilizable
import RecepcionModal from "../components/RecepcionModal"; // Modal con DataTableExpandible y detalle de remisiones

const PlanifLinesTableros = () => {
    const [orders, setOrders] = useState([]);
    const [modalData, setModalData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [projectFilter, setProjectFilter] = useState("");

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const sapUser = JSON.parse(localStorage.getItem("sapUser"));
            const whsCode = sapUser?.WhsCode || "";
            const Company = sapUser?.CompanyDB || "";
            console.log(sapUser?.CompanyDB);
            const response = await apiClient.get("/inventario/recepcion/estado", {
                params: {
                    company: Company
                }
            });

            if (!Array.isArray(response.data)) throw new Error("Formato inválido");

            const grouped = response.data.reduce((acc, item) => {
                const key = `${item.CardCode}_${item.DocEntry}`;
                if (!acc[key]) acc[key] = { ...item, Orders: [] };
                acc[key].Orders.push(item);
                return acc;
            }, {});

            setOrders(Object.values(grouped));
        } catch (error) {
            console.error("Error cargando órdenes:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const getFechaRemOrden = (order) => {
        // Retorna la fecha de remisión más temprana del pedido, si existe; si no, usa ShipDate de la primera línea
        const fechas = (order.Orders || [])
            .map(l => l.FechaRem || l.ShipDate)
            .filter(Boolean);
        if (fechas.length === 0) return "";
        const parseUTC = (s) => {
            const str = String(s).trim();
            const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (m) {
                const [, y, mm, dd] = m;
                return Date.UTC(Number(y), Number(mm) - 1, Number(dd));
            }
            const d = new Date(str);
            if (isNaN(d)) return null;
            return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
        };
        const minTs = fechas.reduce((min, cur) => {
            const ts = parseUTC(cur);
            return ts === null ? min : (min === null ? ts : Math.min(min, ts));
        }, null);
        if (minTs === null) return "";
        const d = new Date(minTs);
        return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
    };

    const applyFilters = (orders) => {
        const term = searchTerm.toLowerCase();
        const proj = projectFilter.toLowerCase();

        return orders.filter(order => {
            const nro = String(order.DocNum || "").toLowerCase();
            const proveedor = String(order.CardName || "").toLowerCase();
            const fecha = getFechaRemOrden(order) || formatDate(order.Orders[0]?.ShipDate);
            const comentario = String(order.Comments || "").toLowerCase();

            const matchesSearch = !term || nro.includes(term) || proveedor.includes(term) || fecha.includes(term);
            const matchesProject = !proj || comentario.includes(proj);

            return matchesSearch && matchesProject;
        });
    };

    const categorizeOrders = (filteredOrders) => {
        const pendientes = [], atrasados = [], parcial = [], recibidos = [], canceladas = [];
        const today = new Date();

        filteredOrders.forEach(order => {
            const isCanceled = (() => {
                const v = order.CANCELED ?? order.Canceled ?? order.Cancelled ?? order.CANCELED_FLAG;
                if (v == null) return (order.Orders || []).some(l => (l.CANCELED ?? l.Canceled ?? l.Cancelled) === 'Y' || String(l.CANCELED ?? l.Canceled ?? l.Cancelled).toLowerCase() === 'tyes');
                const s = String(v).trim().toLowerCase();
                return s === 'y' || s === 'yes' || s === 'tyes' || s === 'true' || s === '1';
            })();

            const allPending = order.Orders.every(l => parseFloat(l.Quantity) === parseFloat(l.OpenQty));
            const allReceived = order.Orders.every(l => parseFloat(l.OpenQty) === 0);
            const anyRemision = order.Orders.some(l => (l.DocNumRem && String(l.DocNumRem).trim() !== '') || (l.NroRemision && String(l.NroRemision).trim() !== '') || (parseFloat(l.CantidadRecibida || 0) > 0));

            const hasLate = order.Orders.some(l => {
                const fechaRecep = new Date(l.ShipDate);
                return fechaRecep < today && parseFloat(l.OpenQty) === parseFloat(l.Quantity);
            });

            const partiallyReceived = order.Orders.some(l => {
                const qty = parseFloat(l.Quantity);
                const pend = parseFloat(l.OpenQty);
                return pend > 0 && pend < qty;
            });

            if (isCanceled) canceladas.push(order);
            else if (hasLate) atrasados.push(order);
            else if (partiallyReceived) parcial.push(order);
            else if (allPending) pendientes.push(order);
            else if (allReceived && anyRemision) recibidos.push(order);
            else pendientes.push(order);
        });

        return { pendientes, atrasados, parcial, recibidos, canceladas };
    };

    const filtered = applyFilters(orders);
    const { pendientes, atrasados, parcial, recibidos, canceladas } = categorizeOrders(filtered);

    const handleCardClick = (order) => {
        setModalData(order);
    };

    return (
        <div className="container-fluid">
            <h2 className="text-center mb-3">Estados de Ordenes de Compras</h2>
            <div className="row mb-3">
                <div className="col-md-6">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por Nro Documento, Fecha o Proveedor"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="col-md-6">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Filtrar por Proyecto"
                        value={projectFilter}
                        onChange={(e) => setProjectFilter(e.target.value)}
                    />
                </div>
            </div>
            {loading && <div className="text-center">Cargando...</div>}
            <Kanban
                columnsConfig={[
                    { title: "Canceladas", data: canceladas },
                    { title: "Pendientes", data: pendientes },
                    { title: "Atrasados", data: atrasados },
                    { title: "Recibido Parcialmente", data: parcial },
                    { title: "Cumplidos", data: recibidos },
                ]}
                cardRenderer={(order) => (
                    <div className="card p-2" onClick={() => handleCardClick(order)}>
                        <div className="d-flex align-items-center justify-content-between">
                            <strong className="text-truncate">{order.CardName}</strong>
                            {(() => {
                                const v = order.CANCELED ?? order.Canceled ?? order.Cancelled ?? order.CANCELED_FLAG;
                                const s = v == null ? '' : String(v).trim().toLowerCase();
                                const isCanceled = s === 'y' || s === 'yes' || s === 'tyes' || s === 'true' || s === '1';
                                return isCanceled ? (
                                    <span style={{ backgroundColor: '#f8d7da', color: '#842029', border: '1px solid #f5c2c7', borderRadius: 999, padding: '0 .4rem', fontSize: 12 }}>Cancelada</span>
                                ) : null;
                            })()}
                        </div>
                        <div>Nro: {order.DocNum}</div>
                        <div>Recepción: {getFechaRemOrden(order) || formatDate(order.Orders[0].ShipDate)}</div>
                        <div className="text-truncate">Proyecto: {order.Project}</div>
                        <div className="text-truncate">{order.Comments}</div>
                    </div>
                )}
            />

            {modalData && (
                <RecepcionModal
                    order={modalData}
                    onClose={() => setModalData(null)}
                />
            )}
        </div>
    );
};

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const s = String(dateStr).trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
        const [, y, mm, dd] = m;
        return `${dd}/${mm}/${y}`;
    }
    const d = new Date(s);
    if (isNaN(d)) return s;
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year}`;
};

export default PlanifLinesTableros;